import { Warning, Eye, GitDiff, CheckCircle, ArrowUUpLeft, ArrowCounterClockwise, Database } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { AiProgressBanner } from '@/components/AiProgressBanner';
import { VL } from './digestVersionI18n';
import { LifecycleBadge } from './DigestVersionBadge';

const STEPS = ['エンジニア別サマリー', 'プロジェクト別サマリー', '経営向けサマリー', '整理・保存'];

// 候補バージョンの状態バナー — 生成中（有効版は閲覧可）/ 準備完了 / 失敗 / 有効版なし
export function DigestCandidateBanner({ detail, lang, can, onView, onCompare, onActivate, onCancel }) {
  const L = VL[lang] || VL.ja;
  const vs = detail?.versions_summary;
  const cand = vs?.latest_candidate;
  if (!vs?.versioned) return null;
  const hasActive = !!detail.active_version_id;
  if (cand && ['running', 'queued'].includes(cand.status)) {
    return (
      <div data-testid="digest-candidate-generating">
        <AiProgressBanner progress={cand.progress} steps={STEPS} since={cand.started_at} tone="violet"
          defaultLabel={L.candGenerating(cand.version_number)}
          footer={hasActive ? L.candGenerating(cand.version_number) : '他のページに移動しても生成はバックグラウンドで続行されます（通常 1〜2 分）。'}
          testIdPrefix="digest-progress" onCancel={onCancel} />
      </div>
    );
  }
  if (cand && ['complete', 'complete_with_warnings'].includes(cand.status)) {
    return (
      <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="digest-candidate-ready">
        <CheckCircle size={18} weight="duotone" className="text-indigo-600 shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <p className="text-[13px] font-semibold text-indigo-900 flex items-center gap-2">
            {hasActive ? L.candReady(cand.version_number) : L.noActiveTitle} <LifecycleBadge v={cand} lang={lang} testId="digest-candidate-badge" />
            {cand.status === 'complete_with_warnings' && <span className="text-[11px] text-amber-700 font-semibold">⚠ {L.warnings}</span>}
          </p>
          <p className="text-[11px] text-indigo-700 mt-0.5">{hasActive ? L.candReadyHint : L.noActiveHint}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => onView(cand)} data-testid="digest-candidate-view-btn">
            <Eye size={13} className="mr-1" /> {L.view}
          </Button>
          {hasActive && can.compare && (
            <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => onCompare(detail.active_version_id, cand.id)} data-testid="digest-candidate-compare-btn">
              <GitDiff size={13} className="mr-1" /> {L.compare}
            </Button>
          )}
          {can.activate && cand.activatable && (
            <Button size="sm" className="h-8 text-[12px] bg-emerald-700 hover:bg-emerald-800" onClick={() => onActivate(cand)} data-testid="digest-candidate-activate-btn">
              <CheckCircle size={13} className="mr-1" /> {L.activate}
            </Button>
          )}
        </div>
      </div>
    );
  }
  if (cand && cand.status === 'failed' && hasActive) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3" data-testid="digest-candidate-failed">
        <Warning size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-rose-900">{L.candFailed(cand.version_number)}</p>
          {cand.error && <p className="text-[11px] text-rose-700 mt-0.5 whitespace-pre-wrap">{cand.error}</p>}
        </div>
      </div>
    );
  }
  return null;
}

// 履歴 / 候補バージョンを表示中（読み取り専用）
export function DigestVersionBanner({ version, lang, can, activeVersionId, onBack, onActivate, onRestore, onCompare }) {
  const L = VL[lang] || VL.ja;
  if (!version) return null;
  return (
    <div className="rounded-xl border-2 border-indigo-300 bg-indigo-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="digest-version-banner">
      <Eye size={18} weight="duotone" className="text-indigo-700 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <p className="text-[13px] font-semibold text-indigo-900 flex items-center gap-2" data-testid="digest-version-banner-title">
          {L.viewingBanner(version)} <LifecycleBadge v={version} lang={lang} testId="digest-version-banner-badge" />
        </p>
        <p className="text-[11px] text-indigo-700 mt-0.5">{L.viewingHint}</p>
        {version.operation_id && (
          <p className="text-[10px] text-indigo-500 mt-0.5 font-mono" data-testid="digest-version-banner-op">
            {L.aiOpsId}: {version.operation_id}
            {version.ai_operation_link && <a href={version.ai_operation_link} target="_blank" rel="noreferrer" className="ml-1 underline">{L.aiOps}</a>}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {activeVersionId && can.compare && (
          <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => onCompare(activeVersionId, version.id)} data-testid="digest-version-banner-compare">
            <GitDiff size={13} className="mr-1" /> {L.compare}
          </Button>
        )}
        {can.activate && version.activatable && (
          <Button size="sm" className="h-8 text-[12px] bg-emerald-700 hover:bg-emerald-800" onClick={() => onActivate(version)} data-testid="digest-version-banner-activate">
            <CheckCircle size={13} className="mr-1" /> {L.activate}
          </Button>
        )}
        {can.restore && version.integrity_ok !== false && ['complete', 'complete_with_warnings'].includes(version.status) && (
          <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => onRestore(version)} data-testid="digest-version-banner-restore">
            <ArrowUUpLeft size={13} className="mr-1" /> {L.restore}
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-8 text-[12px]" onClick={onBack} data-testid="digest-version-banner-back">
          <ArrowCounterClockwise size={13} className="mr-1" /> {L.backToActive}
        </Button>
      </div>
    </div>
  );
}

// 旧形式（未移行）— 管理者のみ移行を実行できる
export function DigestLegacyBanner({ lang, isAdmin, busy, onDryRun, onMigrate }) {
  const L = VL[lang] || VL.ja;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="digest-legacy-banner">
      <Database size={18} weight="duotone" className="text-amber-700 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <p className="text-[13px] font-semibold text-amber-900">{L.legacyTitle}</p>
        <p className="text-[11px] text-amber-800 mt-0.5">{L.legacyHint}</p>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 text-[12px]" disabled={busy} onClick={onDryRun} data-testid="digest-migrate-dry-btn">{L.migrateDry}</Button>
          <Button size="sm" className="h-8 text-[12px] bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={onMigrate} data-testid="digest-migrate-run-btn">{L.migrateRun}</Button>
        </div>
      )}
    </div>
  );
}
